import {
  Badge,
  BadgeProgress,
  BadgeQuest,
  BadgesApiResponse,
  BadgeReward,
  QuestTask,
} from "./types";

const DEFAULT_BADGE_PROGRESS: BadgeProgress = {
  segmentId: "",
  promoLevelAward: [],
  quests: [],
};

const isBadgeGroupsPayload = (payload: any): payload is BadgesApiResponse => {
  return Array.isArray(payload?.badgeGroups);
};

const isEntityActive = (status?: string | null, startDate?: string | null, endDate?: string | null) => {
  const now = Date.now();

  if (status && status !== "ACTIVE") return false;

  if (startDate && Number.isFinite(new Date(startDate).getTime())) {
    if (new Date(startDate).getTime() > now) return false;
  }

  if (endDate && Number.isFinite(new Date(endDate).getTime())) {
    if (new Date(endDate).getTime() < now) return false;
  }

  return true;
};

const toQuestTask = (task: any): QuestTask => ({
  id: Number(task?.id || 0),
  stageId: Number(task?.stageId || 0),
  segments: task?.segments ?? null,
  taskType: task?.taskType || "",
  aggregateType: task?.aggregateType || "",
  aggregateValue: Number(task?.aggregateValue || 0),
  multiplierTimes:
    task?.multiplierTimes === undefined || task?.multiplierTimes === null || task?.multiplierTimes === ""
      ? Number(task?.taskType === "Win_multiplier" ? task?.aggregateValue || 0 : 0)
      : Number(task.multiplierTimes),
  minAmount: Number(task?.minAmount || 0),
  extProviderIds: Array.isArray(task?.extProviderIds) ? task.extProviderIds : [],
  extGameIds: Array.isArray(task?.extGameIds) ? task.extGameIds : [],
  providerNames: Array.isArray(task?.providerNames) ? task.providerNames : [],
  checkpoints: task?.checkpoints ?? null,
  casinoReward: Array.isArray(task?.casinoReward) ? task.casinoReward : [],
});

const toBadgeReward = (reward: any): BadgeReward => ({
  id: Number(reward?.id || 0),
  casinoId: Number(reward?.casinoId || 0),
  organizationId: Number(reward?.organizationId || 0),
  segmentId: reward?.segmentId ?? null,
  typeId: Number(reward?.typeId || 0),
  name: reward?.name || "",
  value: Number(reward?.value || 0),
  gameId: reward?.gameId ?? null,
  multiplier: reward?.multiplier ?? null,
  rewardType: reward?.rewardType || "",
  section: reward?.section ?? null,
  image: reward?.image || "",
  minValue: reward?.minValue ?? null,
  maxValue: reward?.maxValue ?? null,
});

const toBadgeQuest = (quest: any): BadgeQuest => ({
  id: Number(quest?.id || 0),
  stepId: Number(quest?.stepId || 0),
  unlocks: Array.isArray(quest?.unlocks) ? quest.unlocks : [],
  startDate: quest?.startDate || new Date().toISOString(),
  endDate: quest?.endDate ?? null,
  activeDays: Array.isArray(quest?.activeDays) ? quest.activeDays : [],
  processed: quest?.processed ?? null,
  casinoReward: Array.isArray(quest?.casinoReward) ? quest.casinoReward.map(toBadgeReward) : [],
  task: Array.isArray(quest?.task) ? quest.task.map(toQuestTask) : [],
});

const toBadgeFromQuest = (quest: any, groupId: string): Badge | null => {
  const badge = quest?.badge;
  if (!badge) return null;

  if (!isEntityActive(badge?.status, badge?.startDate ?? quest?.startDate, badge?.endDate ?? quest?.endDate)) {
    return null;
  }

  return {
    orderingScore: String(badge?.orderingScore ?? quest?.stepId ?? 0),
    id: Number(badge?.id ?? quest?.id ?? 0),
    orgUuid: badge?.orgUuid || "",
    name: badge?.name || '{"en":"Badge"}',
    image: badge?.image || "",
    startDate: badge?.startDate || quest?.startDate || new Date().toISOString(),
    endDate: badge?.endDate ?? quest?.endDate ?? null,
    groupId,
    status: badge?.status || "ACTIVE",
    quest: toBadgeQuest(quest),
  };
};

export const normalizeBadgeLobbyResponse = (payload: any): BadgesApiResponse => {
  if (isBadgeGroupsPayload(payload)) {
    return {
      ...payload,
      progress: payload.progress || DEFAULT_BADGE_PROGRESS,
      exRates: Array.isArray(payload.exRates) ? payload.exRates : [],
      promoRef: payload.promoRef || "",
    };
  }

  const groupId = `group-${payload?.id || "default"}`;
  const badges = (Array.isArray(payload?.quest) ? payload.quest : [])
    .map((quest: any) => toBadgeFromQuest(quest, groupId))
    .filter(Boolean) as Badge[];

  badges.sort((a, b) => {
    const orderingDiff = Number(a.orderingScore || 0) - Number(b.orderingScore || 0);
    if (orderingDiff !== 0) return orderingDiff;
    return (a.quest?.stepId || 0) - (b.quest?.stepId || 0);
  });

  return {
    badgeGroups: [
      {
        id: groupId,
        orgUuid: payload?.orgUuid || "",
        translations: payload?.name || '{"en":"Badges"}',
        badges,
      },
    ],
    progress: payload?.progress || DEFAULT_BADGE_PROGRESS,
    exRates: Array.isArray(payload?.exRates) ? payload.exRates : [],
    promoRef: payload?.promoRef || "",
  };
};
